import { firebaseDb } from './init'
import {
  QueryConstraint,
  collection,
  doc,
  getDocs,
  query,
  where,
} from 'firebase/firestore'
import { QuerySnapshot } from 'firebase-admin/firestore'
import { firebaseGetSingleDocFromDb } from './utils'
import {
  IGenericError,
  IRecommendedPaper,
  IRecommendedPaperEntity,
  IRecommendedPaperRaw,
} from '../types'

const recommendedPapersCollectionRef = collection(
  firebaseDb,
  'recommendedPapers',
)

const recommendedPapersDocRef = (id: string) =>
  doc(firebaseDb, 'recommendedPapers', id)

export async function getRecommendedPapersByModuleId(
  queryConstraints: QueryConstraint[] = [],
): Promise<IRecommendedPaper[]> {
  try {
    const q = query(recommendedPapersCollectionRef, ...queryConstraints)

    const querySnapshot = (await getDocs(
      q,
    )) as unknown as QuerySnapshot<IRecommendedPaperRaw>

    const recommendedPapers: IRecommendedPaper[] = []

    querySnapshot.docs.forEach((doc) => {
      const document: IRecommendedPaper = {
        ...doc.data(),
        id: doc.id,
      }

      recommendedPapers.push(document)
    })

    return recommendedPapers
  } catch (error) {
    console.error(
      `error while tying to get recommended papers :>> `,
      (error as IGenericError)?.message || error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while tying to get recommended papers`,
    )
  }
}

export async function getRecommendedPapersEntitiesByModuleId<T, K>(
  moduleId: string,
  courseId: string,
): Promise<IRecommendedPaperEntity[]> {
  try {
    const queryConstraints: QueryConstraint[] = [
      where('moduleId', '==', moduleId),
      where('courseId', '==', courseId),
    ]

    const recommendedPapers = await getRecommendedPapersByModuleId(
      queryConstraints,
    )

    const tasks = recommendedPapers.map(async (rp: IRecommendedPaper) => {
      const singleDoc: K | undefined = await firebaseGetSingleDocFromDb<T, K>(
        rp.type,
        rp.paperId,
      )

      if (singleDoc) {
        const document = {
          collection: rp.type,
          ...singleDoc,
        } as unknown as IRecommendedPaperEntity

        return document
      }

      return null
    })

    const results: (IRecommendedPaperEntity | null)[] = await Promise.all(tasks)

    const papers = results.filter(
      (doc): doc is IRecommendedPaperEntity => doc !== null,
    )

    return papers
  } catch (error) {
    console.error(
      `error while tying to get recommended papers by moduleId: ${moduleId}, courseId: ${courseId} :>> `,
      (error as IGenericError)?.message || error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while tying to get recommended papers`,
    )
  }
}
